import { ChevronRight, type LucideIcon } from "lucide-react"
import type { MouseEvent } from "react"

type BaseProps = {
  icon: LucideIcon
  title: string
  description?: string
  meta?: string
}

type ActionRowProps = BaseProps & {
  href?: string
  onClick?: (
    event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>
  ) => void
  external?: boolean
  ariaLabel?: string
}

export function ActionRow({
  icon: Icon,
  title,
  description,
  meta,
  href,
  onClick,
  external,
  ariaLabel,
}: ActionRowProps) {
  const inner = (
    <>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-stone-100 text-stone-600 transition group-hover:bg-orange-50 group-hover:text-orange-600">
        <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[14px] font-semibold leading-5 text-stone-900">
          {title}
        </span>
        {description ? (
          <span className="mt-0.5 block text-[12.5px] leading-5 text-stone-500">
            {description}
          </span>
        ) : null}
      </span>
      {meta ? (
        <span className="text-[12.5px] font-medium text-stone-500 tabular-nums">
          {meta}
        </span>
      ) : null}
      <ChevronRight
        className="h-4 w-4 shrink-0 text-stone-400 transition group-hover:translate-x-0.5 group-hover:text-orange-600"
        aria-hidden="true"
      />
    </>
  )

  const className =
    "group flex min-h-[64px] w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition hover:bg-stone-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"

  if (href) {
    return (
      <a
        href={href}
        onClick={onClick}
        className={className}
        aria-label={ariaLabel}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
      >
        {inner}
      </a>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={className}
      aria-label={ariaLabel}
    >
      {inner}
    </button>
  )
}
